import { inject, Injectable } from "@angular/core";
import { EventNotificationsService, KsefConfigurationStatusNotificator } from "@skylab-angular-shared/business";
import { Http, QueryBus, RoutingService } from "@skylab-angular-shared/core";
import { ThemeSwitcherService } from "@skylab-angular-shared/ui";
import { EventNotificationsServiceBase, KsefConfigurationStatusItem, KsefConfigurationStatusNotificatorBase, KsefConfigurationStatusQuery } from "@skylab-shared/business";
import { ConsoleLogger, HttpService, TaggedUrl, Tags } from "@skylab-shared/core";
import { KsefConfigurationStatus } from "@skylab-shared/dataModel";
import { AppStates, ConfigurationParametersViews } from "@skylab-shared/scenes";
import { NewWindowManager, WindowUtils } from "@skylab-shared/utils";
import { BehaviorSubject } from "rxjs";
import { KIMessageListenerService } from "../../ki/ki-message-listener.service";

@Injectable({
	providedIn: 'root'
})
export class KsefConfigurationService {
	private readonly http = inject(Http) as HttpService;
	private readonly ksefConfigurationNotifier = inject(KsefConfigurationStatusNotificator) as KsefConfigurationStatusNotificatorBase;
	private readonly eventNotifications = inject(EventNotificationsService) as EventNotificationsServiceBase;
	private readonly themeSwitcher = inject(ThemeSwitcherService);
	private readonly kiMessageListener = inject(KIMessageListenerService);
	private readonly routingService = inject(RoutingService);

	private readonly ksefStatus = new BehaviorSubject<KsefConfigurationStatusItem>({status: KsefConfigurationStatus.INACTIVE, description: '', reason: ''});

	private readonly queryBus = inject(QueryBus);

	public ksefStatus$ = this.ksefStatus.asObservable();

	private ksefCreatorUrl!: string;
	private ksefWindow: NewWindowManager | null = null;

	private logger = new ConsoleLogger(this.constructor.name);

	public constructor() {
		this.refreshKsefStatusAsync();
		this.ksefConfigurationNotifier.addObserver(() => {
			this.refreshKsefStatusAsync();
		});

		this.kiMessageListener.messageListener.subscribe((e) => {
			if (e.type !== "KSEF_WINDOW_EVENT") return;

			if (e.action === "REFRESH_CONFIGURATION_STATUS") {
				this.refreshKsefStatusAsync();
				this.refreshKsefStatusEvent();
			} else if (e.action === "OPEN_KSEF_OFFLINE_PARAMS") {
				this.routingService.navigate([AppStates.configurationParameters.url], { queryParams: { view: ConfigurationParametersViews.KsefSettings } });
                this.ksefWindow?.close();
			}
		})
	}

	public async showKsefCreatorAsync(): Promise<void> {
		if (!this.ksefWindow) {
			const baseUrl = await this.getKsefCreatorUrlAsync();
			if (!baseUrl) return;
			this.ksefWindow = WindowUtils.createNewWindowManager({height: 800, width: 1000, getUrl: () => baseUrl + `&theme=${this.themeSwitcher.currentTheme}`});
		}

		this.ksefWindow.openOrFocus(() => {
			this.refreshKsefStatusAsync();
			this.refreshKsefStatusEvent();
			this.ksefWindow = null;
		});
	}

	public async refreshKsefStatusAsync(): Promise<KsefConfigurationStatusItem | undefined> {
		try {
			const data = await this.queryBus.queryAsync(new KsefConfigurationStatusQuery());
			this.ksefStatus.next(data);
			return data;
		} catch {
			this.logger.error("Unable to get configuration status");
			return undefined;
		}
	}

	private refreshKsefStatusEvent(): void {
		this.eventNotifications.refreshInterval('ksefConfigurationStatusUpdate');
	}

	private async getKsefCreatorUrlAsync(): Promise<string> {
		const url = TaggedUrl.join('user/ksef/panel-url', [Tags.Api]);
		const response = await this.http.get<{url: string}>(url);

		this.ksefCreatorUrl = response.data.url;
		return this.ksefCreatorUrl;
	}

	public isNoProcessingKsefStatus(status: KsefConfigurationStatus | undefined): boolean {
		return [KsefConfigurationStatus.ERROR, KsefConfigurationStatus.INACTIVE, KsefConfigurationStatus.BLOCKED].includes(status!);
	}
}